import {getAcceptedKycDocumentTypes, isPassportIdentity} from '@sollapay/domain';
import {
  SellerType,
  DocumentContext,
  DocumentEntityType,
  IdentityDocumentType,
  PartyType,
  RelationshipType
} from '@sollapay/enums';
import {
  CorpIdField,
  FieldDatePicker,
  FieldSelect,
  FormSectionHeading,
  Icon,
  Typography
} from '@sollapay/ui/components';
import {generateCompanyId} from '@sollapay/utils';
import {Controller, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';

import {FullNameField, LOCKED_FLEX_COL_CLASSNAME, ReadOnlyFieldset} from '@/components';
import {
  IdentityDocumentFields,
  IdentityTypeToggle,
  identityFieldsCellClassName
} from '@/components/identity';
import {PepSection, PepToggleField, usePepParties} from '@/components/pep';
import {ControlledAddressField} from '@/features/address';
import {
  AiExtractionStatus,
  AiFlashWrapper,
  useAiFillFlash,
  useFieldAiProps
} from '@/features/ai-extraction';
import {TaxResidencyFields} from '@/features/buyers/components';
import {
  generateDateOfBirth,
  generateHebrewCorpName,
  generateHebrewPersonName,
  generateIncorporationDate,
  generateIsraeliAddress,
  generateIdentityNumber,
  withDebugFieldChange,
  withDebugSetValue
} from '@/features/debug-test-data';
import {buildDocumentTab, DocumentsAutoFillTip, DocumentUploadTabs} from '@/features/documents';
import {futureDateRule} from '@/lib/validation';

import {SellerSideRoleSection} from './SellerSideRoleSection';
import {SellerTypeSection} from './SellerTypeSection';

import type {SellerFormValues, SellersFormValues} from '../utils';
import type {AiSuggestion} from '@/features/ai-extraction';
import type {DebugSetValue} from '@/features/debug-test-data';
import type {PartyRole} from '@sollapay/enums';
import type {UseFormReturn} from 'react-hook-form';

export interface SellerDetailsRowProps {
  form: UseFormReturn<SellersFormValues>;
  index: number;
  trustAccountId: string;
  readOnly: boolean;
  asksRelationshipQuestions: boolean;
  asksSellerRole: boolean;
  sellerRoleOptions: PartyRole[];
  fieldErrors: Record<string, string | undefined>;
  showErrors: boolean;
  aiFields: Map<string, AiSuggestion>;
  isProcessing: boolean;
  suggestionsCount: number;
  markFieldModified: (field: string) => void;
  onFillManually: () => void;
  documentsReadOnly?: boolean;
  onDocumentChange: () => void;
}

export const SellerDetailsRow = ({
  form,
  index,
  trustAccountId,
  readOnly,
  asksRelationshipQuestions,
  asksSellerRole,
  sellerRoleOptions,
  fieldErrors,
  showErrors,
  aiFields,
  isProcessing,
  suggestionsCount,
  markFieldModified,
  onFillManually,
  documentsReadOnly,
  onDocumentChange
}: SellerDetailsRowProps) => {
  const {t} = useTranslation(['trustAccounts', 'common']);
  const {control, register} = form;
  const setDebugValue = form.setValue as DebugSetValue;
  const rp = <K extends keyof SellerFormValues & string>(field: K) =>
    `sellers.${index}.${field}` as `sellers.0.${K}`;

  /** Takes a fully-qualified path (e.g. `rp('idNumber')` or `${pepPath}.parties`) — `fieldErrors`
   * is keyed by the full dot path since the form wraps all sellers in one array schema. */
  const fe = (key: string) => {
    const message = fieldErrors[key];
    return showErrors && message ? t(message) : undefined;
  };
  const fieldAi = useFieldAiProps({
    aiFields,
    isProcessing,
    markFieldModified,
    getValue: form.getValues
  });
  const isFlashing = useAiFillFlash(aiFields);

  const [sellerId, selectedSellerType, selectedIdType] = useWatch({
    control,
    name: [rp('id'), rp('sellerType'), rp('idType')]
  }) as [string | undefined, SellerType | undefined, IdentityDocumentType | undefined];
  const isCorporation = selectedSellerType === SellerType.CORPORATION;
  const idType = selectedIdType ?? IdentityDocumentType.NATIONAL_ID;
  const isPassport = !isCorporation && isPassportIdentity(idType);

  const pepParties = usePepParties({
    control,
    isCorporate: isCorporation,
    pepPath: rp('pep')
  });

  const fullNameLabel = isCorporation
    ? t('activation.packages.trustDetails.form.fields.fullNameCorporate')
    : t('activation.packages.trustDetails.form.fields.fullName');
  const fullNamePlaceholder = isCorporation
    ? t('activation.packages.trustDetails.form.fields.fullNameCorporatePlaceholder')
    : t('activation.packages.trustDetails.form.fields.fullNamePlaceholder');
  const idNumberLabel = isCorporation
    ? t('activation.packages.trustDetails.form.fields.idNumberCorporate')
    : t('activation.packages.trustDetails.form.fields.idNumberIndividual');
  const idNumberPlaceholder = isCorporation
    ? t('activation.packages.trustDetails.form.fields.idNumberCorporatePlaceholder')
    : t('activation.packages.trustDetails.form.fields.idNumberPlaceholder');
  const dateFieldLabel = isCorporation
    ? t('activation.packages.trustDetails.form.fields.birthOrIncorporationDate')
    : t('activation.packages.trustDetails.form.fields.dateOfBirth');

  const relationshipTypeOptions = [
    {
      value: RelationshipType.FIRST_PROJECT,
      label: t('activation.packages.trustDetails.form.options.relationshipTypes.firstProject')
    },
    {
      value: RelationshipType.FEW_PROJECTS,
      label: t('activation.packages.trustDetails.form.options.relationshipTypes.fewProjects')
    },
    {
      value: RelationshipType.ONGOING_COLLAB,
      label: t('activation.packages.trustDetails.form.options.relationshipTypes.ongoingCollab')
    }
  ];

  const metFaceToFaceOptions = [
    {value: 'yes', label: t('activation.packages.trustDetails.form.options.metFaceToFace.yes')},
    {value: 'no', label: t('activation.packages.trustDetails.form.options.metFaceToFace.no')}
  ];

  const idNumberAi = fieldAi(rp('idNumber'));
  const idCountryAi = fieldAi(rp('idCountry'));

  const addressField = (
    <AiFlashWrapper field={rp('address')} fieldAi={fieldAi} isFlashing={isFlashing}>
      {ai => (
        <ControlledAddressField
          control={control}
          name={rp('address')}
          label={t('activation.packages.trustDetails.form.fields.address')}
          validateRequired={false}
          cornerBadge={withDebugSetValue(
            ai.aiIndicator,
            setDebugValue,
            rp('address'),
            generateIsraeliAddress,
            ai.onAiChange
          )}
          isLoading={ai.aiGenerating}
          error={fe(rp('address'))}
          onAiChange={ai.onAiChange}
        />
      )}
    </AiFlashWrapper>
  );

  const idField = isCorporation ? (
    <AiFlashWrapper flashing={isFlashing(rp('idNumber'))}>
      <Controller
        name={rp('idNumber')}
        control={control}
        render={({field}) => (
          <CorpIdField
            label={idNumberLabel}
            required
            placeholder={idNumberPlaceholder}
            description={t('activation.packages.trustDetails.form.fields.idNumberCorporateHelp')}
            cornerBadge={withDebugFieldChange(
              idNumberAi.aiIndicator,
              field.onChange,
              generateCompanyId,
              idNumberAi.onAiChange
            )}
            isLoading={idNumberAi.aiGenerating}
            error={fe(rp('idNumber'))}
            value={field.value as string}
            onChange={value => {
              field.onChange(value);
              idNumberAi.onAiChange();
            }}
            onBlur={field.onBlur}
          />
        )}
      />
    </AiFlashWrapper>
  ) : (
    <AiFlashWrapper
      flashing={isFlashing(rp('idNumber')) || isFlashing(rp('idCountry'))}
      className={identityFieldsCellClassName(isPassport)}
    >
      <IdentityDocumentFields
        control={control}
        isPassport={isPassport}
        idNumberName={rp('idNumber')}
        idCountryName={rp('idCountry')}
        idNumberError={fe(rp('idNumber'))}
        idCountryError={fe(rp('idCountry'))}
        cornerBadge={withDebugSetValue(
          idNumberAi.aiIndicator,
          setDebugValue,
          rp('idNumber'),
          () => generateIdentityNumber(idType),
          idNumberAi.onAiChange
        )}
        isLoading={idNumberAi.aiGenerating}
        onIdNumberChange={idNumberAi.onAiChange}
        idCountryCornerBadge={idCountryAi.aiIndicator}
        idCountryIsLoading={idCountryAi.aiGenerating}
        onIdCountryChange={idCountryAi.onAiChange}
      />
    </AiFlashWrapper>
  );

  const dateField = (
    <AiFlashWrapper
      field={rp('birthOrIncorporationDate')}
      fieldAi={fieldAi}
      isFlashing={isFlashing}
    >
      {ai => (
        <Controller
          name={rp('birthOrIncorporationDate')}
          control={control}
          rules={futureDateRule(t)}
          render={({field}) => (
            <FieldDatePicker
              label={dateFieldLabel}
              required
              disableFuture
              placeholder={t('activation.packages.trustDetails.form.fields.datePlaceholder')}
              value={field.value as string}
              onChange={value => {
                field.onChange(value);
                ai.onAiChange();
              }}
              error={fe(rp('birthOrIncorporationDate'))}
              startAdornment={<Icon name="calendar" className="size-4" />}
              cornerBadge={withDebugFieldChange(
                ai.aiIndicator,
                field.onChange,
                () => (isCorporation ? generateIncorporationDate() : generateDateOfBirth()),
                ai.onAiChange
              )}
              isLoading={ai.aiGenerating}
            />
          )}
        />
      )}
    </AiFlashWrapper>
  );

  const documentMetadata = sellerId ? {sellerId} : undefined;
  const documentTabs = getAcceptedKycDocumentTypes({
    partyType: isCorporation ? PartyType.ORGANIZATION : PartyType.PERSON,
    idType
  }).map(documentType => buildDocumentTab(documentType, t));

  return (
    <div className="flex flex-col gap-6">
      <ReadOnlyFieldset readOnly={readOnly} lockedClassName={LOCKED_FLEX_COL_CLASSNAME}>
        {asksSellerRole ? (
          <SellerSideRoleSection
            control={control}
            sellerTypeName={rp('sellerType')}
            sellerRoleName={rp('sellerRole')}
            sellerRoleOptions={sellerRoleOptions}
            sellerRoleError={fe(rp('sellerRole'))}
            titleSize="sm"
          />
        ) : (
          <SellerTypeSection control={control} sellerTypeName={rp('sellerType')} titleSize="sm" />
        )}

        {!isCorporation && (
          <Controller
            name={rp('idType')}
            control={control}
            render={({field}) => <IdentityTypeToggle value={idType} onChange={field.onChange} />}
          />
        )}
      </ReadOnlyFieldset>

      <div className="flex flex-col gap-6">
        <FormSectionHeading
          titleSize="sm"
          title={t('activation.packages.trustDetails.sellerDetails.sections.documents.title')}
          description={t(
            'activation.packages.trustDetails.sellerDetails.sections.documents.description'
          )}
        />
        {sellerId ? (
          <>
            <DocumentsAutoFillTip />
            <DocumentUploadTabs
              entityType={DocumentEntityType.TRUST_ACCOUNT}
              entityId={trustAccountId}
              documentContext={DocumentContext.TRUST_DETAILS}
              metadata={documentMetadata}
              metadataFilter={documentMetadata}
              readOnly={documentsReadOnly}
              onDocumentChange={onDocumentChange}
              tabs={documentTabs}
            />
          </>
        ) : (
          <Typography size="sm" color="tertiary" className="whitespace-normal">
            {t('activation.packages.trustDetails.sellerDetails.sections.documents.saveFirst')}
          </Typography>
        )}
      </div>

      <ReadOnlyFieldset readOnly={readOnly} lockedClassName={LOCKED_FLEX_COL_CLASSNAME}>
        <div className="flex flex-col gap-4">
          <AiExtractionStatus
            isProcessing={isProcessing}
            suggestionsCount={suggestionsCount}
            onFillManually={onFillManually}
          />

          <FormSectionHeading
            titleSize="sm"
            title={t('activation.packages.trustDetails.sellerDetails.sections.details.title')}
            description={t(
              'activation.packages.trustDetails.sellerDetails.sections.details.description'
            )}
          />

          <div className="grid grid-cols-2 gap-6">
            {asksRelationshipQuestions && (
              <>
                <Controller
                  name={rp('relationshipType')}
                  control={control}
                  render={({field}) => (
                    <div className="col-span-2">
                      <FieldSelect
                        label={t('activation.packages.trustDetails.form.fields.relationshipType')}
                        required
                        placeholder={t(
                          'activation.packages.trustDetails.form.fields.relationshipTypePlaceholder'
                        )}
                        value={field.value as RelationshipType}
                        onValueChange={(value: string) => field.onChange(value as RelationshipType)}
                        options={relationshipTypeOptions}
                        error={fe(rp('relationshipType'))}
                      />
                    </div>
                  )}
                />

                <Controller
                  name={rp('metFaceToFace')}
                  control={control}
                  render={({field}) => (
                    <div className="col-span-2">
                      <FieldSelect
                        label={t('activation.packages.trustDetails.form.fields.metFaceToFace')}
                        required
                        placeholder={t(
                          'activation.packages.trustDetails.form.fields.metFaceToFacePlaceholder'
                        )}
                        value={field.value as string}
                        onValueChange={field.onChange}
                        options={metFaceToFaceOptions}
                        error={fe(rp('metFaceToFace'))}
                      />
                    </div>
                  )}
                />
              </>
            )}

            <div className="col-span-2">
              <AiFlashWrapper field={rp('fullName')} fieldAi={fieldAi} isFlashing={isFlashing}>
                {ai => (
                  <FullNameField
                    label={fullNameLabel}
                    required
                    placeholder={fullNamePlaceholder}
                    description={
                      isCorporation
                        ? t('activation.packages.trustDetails.form.fields.fullNameCorporateHelp')
                        : undefined
                    }
                    cornerBadge={withDebugSetValue(
                      ai.aiIndicator,
                      setDebugValue,
                      rp('fullName'),
                      () => (isCorporation ? generateHebrewCorpName() : generateHebrewPersonName()),
                      ai.onAiChange
                    )}
                    isLoading={ai.aiGenerating}
                    error={fe(rp('fullName'))}
                    {...register(rp('fullName'), {
                      onChange: () => ai.onAiChange()
                    })}
                  />
                )}
              </AiFlashWrapper>
            </div>

            {idField}
            {dateField}
            {isPassport ? addressField : <div className="col-span-2">{addressField}</div>}

            <div className="col-span-2">
              <TaxResidencyFields
                control={control}
                taxResidencyName={rp('taxResidency')}
                taxResidencyCountryName={rp('taxResidencyCountry')}
                taxResidencyError={fe(rp('taxResidency'))}
                taxResidencyCountryError={fe(rp('taxResidencyCountry'))}
                i18nPrefix="activation.packages.trustDetails.form.fields"
                i18nOptionsPrefix="activation.packages.trustDetails.form.options.taxResidency"
                validateRequired={false}
                extra={
                  <PepToggleField
                    control={control}
                    isCorporate={isCorporation}
                    pepPath={rp('pep')}
                    replace={pepParties.replace}
                  />
                }
              />
            </div>
          </div>
        </div>

        <PepSection
          control={control}
          register={register}
          isCorporate={isCorporation}
          pepPath={rp('pep')}
          fe={fe}
          enforceRequired={false}
          fields={pepParties.fields}
          append={pepParties.append}
          remove={pepParties.remove}
        />
      </ReadOnlyFieldset>
    </div>
  );
};
